import jsPDF from 'jspdf';
import { TestResults } from '../../../types';
import { PDF_COLORS, getStatusColor } from '../config/colors';
import { PDF_FONTS, PDF_LAYOUT } from '../config/fonts';
import { PageManager } from '../helpers/pageBreaks';
import { testData } from '../../../data/testData';

export class CategoryResultsComponent {
  private pdf: jsPDF;
  private pageManager: PageManager;

  constructor(pdf: jsPDF, pageManager: PageManager) {
    this.pdf = pdf;
    this.pageManager = pageManager;
  }

  generate(results: TestResults): void {
    this.addSectionHeader();
    this.addCategorySummaryTable(results);
    this.addDetailedCategoryAnalysis(results);
  }

  private addSectionHeader(): void {
    this.pageManager.checkForPageBreak(40);
    
    this.pdf.setFont(PDF_FONTS.primary, 'bold');
    this.pdf.setFontSize(PDF_FONTS.size.heading);
    this.pdf.setTextColor(...PDF_COLORS.primary);
    this.pdf.text(
      'Detailed Category Results',
      PDF_LAYOUT.margins.left,
      this.pageManager.getCurrentY()
    );
    
    this.pageManager.addParagraph();
    
    this.pdf.setFont(PDF_FONTS.primary, 'normal');
    this.pdf.setFontSize(PDF_FONTS.size.body);
    this.pdf.setTextColor(...PDF_COLORS.text.secondary);
    
    const description = this.pdf.splitTextToSize(
      'This section provides a comprehensive breakdown of your performance across all ten export readiness categories. Each category is evaluated based on 20 targeted questions designed to assess your current capabilities and identify areas for improvement.',
      this.pageManager.getContentWidth()
    );
    
    description.forEach((line: string) => {
      this.pdf.text(line, PDF_LAYOUT.margins.left, this.pageManager.getCurrentY());
      this.pageManager.addLine();
    });
    
    this.pageManager.addSection();
  }

  private addCategorySummaryTable(results: TestResults): void {
    this.pageManager.checkForPageBreak(60);
    
    // Table header
    this.pdf.setFont(PDF_FONTS.primary, 'bold');
    this.pdf.setFontSize(PDF_FONTS.size.subheading);
    this.pdf.setTextColor(...PDF_COLORS.primary);
    this.pdf.text(
      'Category Summary Table',
      PDF_LAYOUT.margins.left,
      this.pageManager.getCurrentY()
    );
    
    this.pageManager.addParagraph();
    
    const tableX = PDF_LAYOUT.margins.left;
    const tableY = this.pageManager.getCurrentY();
    const tableWidth = this.pageManager.getContentWidth();
    const rowHeight = 12;
    const colWidths = [tableWidth * 0.5, tableWidth * 0.2, tableWidth * 0.3];
    
    // Table header
    this.pdf.setFillColor(...PDF_COLORS.primary);
    this.pdf.rect(tableX, tableY, tableWidth, rowHeight, 'F');
    
    this.pdf.setFont(PDF_FONTS.primary, 'bold');
    this.pdf.setFontSize(PDF_FONTS.size.small);
    this.pdf.setTextColor(...PDF_COLORS.text.white);
    
    this.pdf.text('Category', tableX + 5, tableY + 8);
    this.pdf.text('Score', tableX + colWidths[0] + 5, tableY + 8);
    this.pdf.text('Status', tableX + colWidths[0] + colWidths[1] + 5, tableY + 8);
    
    let currentY = tableY + rowHeight;
    
    // Sort categories by score (lowest first)
    const sortedCategories = [...results.categories].sort((a, b) => a.percentage - b.percentage);
    
    sortedCategories.forEach((category, index) => {
      const categoryInfo = testData.find(c => c.id === category.categoryId);
      if (!categoryInfo) return;
      
      // Alternate row colors
      if (index % 2 === 0) {
        this.pdf.setFillColor(...PDF_COLORS.background.light);
        this.pdf.rect(tableX, currentY, tableWidth, rowHeight, 'F');
      }
      
      // Category name
      this.pdf.setFont(PDF_FONTS.primary, 'normal');
      this.pdf.setFontSize(PDF_FONTS.size.small);
      this.pdf.setTextColor(...PDF_COLORS.text.primary);
      
      const categoryName = categoryInfo.title.length > 35 
        ? categoryInfo.title.substring(0, 32) + '...'
        : categoryInfo.title;
      
      this.pdf.text(categoryName, tableX + 5, currentY + 8);
      
      // Score
      this.pdf.setFont(PDF_FONTS.primary, 'bold');
      this.pdf.text(
        `${Math.round(category.percentage)}%`,
        tableX + colWidths[0] + 5,
        currentY + 8
      );
      
      // Status
      const statusColor = getStatusColor(category.status);
      this.pdf.setTextColor(...statusColor);
      this.pdf.text(
        category.status,
        tableX + colWidths[0] + colWidths[1] + 5,
        currentY + 8
      );
      
      currentY += rowHeight;
    });
    
    // Table border
    this.pdf.setDrawColor(...PDF_COLORS.text.light);
    this.pdf.rect(tableX, tableY, tableWidth, currentY - tableY, 'S');
    
    this.pageManager.setCurrentY(currentY + 15);
  }

  private addDetailedCategoryAnalysis(results: TestResults): void {
    // Sort categories by score for priority analysis
    const sortedCategories = [...results.categories].sort((a, b) => a.percentage - b.percentage);
    
    sortedCategories.forEach((category, index) => {
      this.addCategoryDetail(category, index + 1);
    });
  }

  private addCategoryDetail(category: any, rank: number): void {
    this.pageManager.checkForPageBreak(60);
    
    const categoryInfo = testData.find(c => c.id === category.categoryId);
    if (!categoryInfo) return;
    
    // Category header box
    const headerHeight = 25;
    const statusColor = getStatusColor(category.status);
    
    this.pdf.setFillColor(...statusColor);
    this.pdf.rect(
      PDF_LAYOUT.margins.left,
      this.pageManager.getCurrentY(),
      this.pageManager.getContentWidth(),
      headerHeight,
      'F'
    );
    
    // Category title
    this.pdf.setFont(PDF_FONTS.primary, 'bold');
    this.pdf.setFontSize(PDF_FONTS.size.subheading);
    this.pdf.setTextColor(...PDF_COLORS.text.white);
    this.pdf.text(
      `${rank}. ${categoryInfo.title}`,
      PDF_LAYOUT.margins.left + 5,
      this.pageManager.getCurrentY() + 15
    );
    
    // Score in header
    this.pdf.text(
      `${Math.round(category.percentage)}% - ${category.status}`,
      PDF_LAYOUT.margins.left + this.pageManager.getContentWidth() - 5,
      this.pageManager.getCurrentY() + 15,
      { align: 'right' }
    );
    
    this.pageManager.setCurrentY(this.pageManager.getCurrentY() + headerHeight + 10);
    
    // Description
    this.pdf.setFont(PDF_FONTS.primary, 'normal');
    this.pdf.setFontSize(PDF_FONTS.size.body);
    this.pdf.setTextColor(...PDF_COLORS.text.secondary);
    
    const description = this.pdf.splitTextToSize(
      categoryInfo.description,
      this.pageManager.getContentWidth() - 10
    );
    
    description.forEach((line: string) => {
      this.pdf.text(line, PDF_LAYOUT.margins.left + 5, this.pageManager.getCurrentY());
      this.pageManager.addLine();
    });
    
    this.pageManager.addLine();
    
    // Performance analysis
    this.addPerformanceAnalysis(category);
    
    this.pageManager.addSection();
  }

  private addPerformanceAnalysis(category: any): void {
    this.pdf.setFont(PDF_FONTS.primary, 'bold');
    this.pdf.setFontSize(PDF_FONTS.size.body);
    this.pdf.setTextColor(...PDF_COLORS.text.primary);
    this.pdf.text(
      'Performance Analysis:',
      PDF_LAYOUT.margins.left + 5,
      this.pageManager.getCurrentY()
    );
    
    this.pageManager.addLine();
    
    this.pdf.setFont(PDF_FONTS.primary, 'normal');
    this.pdf.setFontSize(PDF_FONTS.size.body);
    this.pdf.setTextColor(...PDF_COLORS.text.secondary);
    
    let analysis = '';
    if (category.percentage >= 80) {
      analysis = 'Excellent performance in this category. You demonstrate strong capabilities and readiness. Continue to maintain these standards and consider sharing best practices with other areas of your business.';
    } else if (category.percentage >= 60) {
      analysis = 'Good foundation with room for improvement. You have solid capabilities but should focus on strengthening specific areas to achieve full export readiness.';
    } else {
      analysis = 'Significant improvement needed. This category requires immediate attention and focused development to support your export objectives.';
    }
    
    const analysisLines = this.pdf.splitTextToSize(
      analysis,
      this.pageManager.getContentWidth() - 10
    );
    
    analysisLines.forEach((line: string) => {
      this.pdf.text(line, PDF_LAYOUT.margins.left + 5, this.pageManager.getCurrentY());
      this.pageManager.addLine();
    });
    
    // Score breakdown
    this.pageManager.addLine();
    this.pdf.setFont(PDF_FONTS.primary, 'bold');
    this.pdf.setTextColor(...PDF_COLORS.text.primary);
    this.pdf.text(
      `Detailed Score: ${category.score} out of ${category.totalPossible} points (${Math.round(category.percentage)}%)`,
      PDF_LAYOUT.margins.left + 5,
      this.pageManager.getCurrentY()
    );
    
    this.pageManager.addLine();
  }
}